const REDACTED = "[redacted]";

const SENSITIVE_QUERY_PARAM_NAMES = new Set([
  "token",
  "code",
  "state",
  "access_token",
  "refresh_token",
  "id_token",
  "invitecode",
  "invite_code",
  "password",
  "secret",
  "api_key",
  "apikey",
  "session",
  "session_id",
  "csrf",
  "csrf_token",
]);

const NESTED_PATH_QUERY_PARAM_NAMES = new Set([
  "returnto",
  "return_to",
  "redirect",
  "redirect_uri",
  "redirect_url",
  "next",
  "continue",
  "url",
]);

export const ANALYTICS_URL_PROPERTY_KEYS = [
  "$current_url",
  "$session_entry_url",
  "$referrer",
  "page_url",
] as const;

export const ANALYTICS_PATH_PROPERTY_KEYS = [
  "return_to",
  "returnTo",
] as const;

export const redactSensitiveQueryParams = (
  searchParams: URLSearchParams,
): URLSearchParams => {
  const sanitized = new URLSearchParams();

  for (const [key, value] of searchParams.entries()) {
    const normalizedKey = key.toLowerCase();

    if (SENSITIVE_QUERY_PARAM_NAMES.has(normalizedKey)) {
      sanitized.set(key, REDACTED);
      continue;
    }

    if (NESTED_PATH_QUERY_PARAM_NAMES.has(normalizedKey)) {
      sanitized.set(key, sanitizeNestedPathOrUrl(value));
      continue;
    }

    sanitized.set(key, value);
  }

  return sanitized;
};

const sanitizePathWithSearch = (pathWithSearch: string): string => {
  const queryIndex = pathWithSearch.indexOf("?");
  if (queryIndex === -1) {
    return pathWithSearch;
  }

  const pathname = pathWithSearch.slice(0, queryIndex);
  const search = pathWithSearch.slice(queryIndex + 1);
  const sanitizedSearch = redactSensitiveQueryParams(
    new URLSearchParams(search),
  ).toString();

  return sanitizedSearch ? `${pathname}?${sanitizedSearch}` : pathname;
};

export const sanitizeNestedPathOrUrl = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return trimmed;
  }

  try {
    const decoded = decodeURIComponent(trimmed);

    if (decoded.startsWith("/")) {
      return sanitizePathWithSearch(decoded);
    }

    const parsed = new URL(decoded);
    parsed.search = redactSensitiveQueryParams(parsed.searchParams).toString();
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return sanitizePathWithSearch(trimmed);
  }
};

export const sanitizeAnalyticsUrl = (
  url: string | undefined | null,
): string | undefined => {
  if (!url) {
    return undefined;
  }

  const trimmed = url.trim();
  if (!trimmed) {
    return undefined;
  }

  try {
    const parsed = new URL(
      trimmed,
      typeof window !== "undefined"
        ? window.location.origin
        : "http://localhost",
    );
    parsed.search = redactSensitiveQueryParams(parsed.searchParams).toString();
    return parsed.toString();
  } catch {
    return sanitizePathWithSearch(trimmed);
  }
};

export const sanitizeAnalyticsProperties = (
  properties: Record<string, unknown>,
): Record<string, unknown> => {
  const sanitized = { ...properties };

  for (const key of ANALYTICS_URL_PROPERTY_KEYS) {
    const value = sanitized[key];
    if (typeof value === "string") {
      sanitized[key] = sanitizeAnalyticsUrl(value) ?? value;
    }
  }

  for (const key of ANALYTICS_PATH_PROPERTY_KEYS) {
    const value = sanitized[key];
    if (typeof value === "string") {
      sanitized[key] = sanitizeNestedPathOrUrl(value);
    }
  }

  return sanitized;
};
